/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import i18n from '@config/i18n/config';
import { TOffsetTag } from '@global-types/index';
import { apiClient } from '@lib/api-client';
import {
	queryClient,
	TMutationConfig,
} from '@lib/react-query';
import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';

/* -------------------------------------------------------------------------- */
/*                                     Types                                   */
/* -------------------------------------------------------------------------- */
export interface ITagValue {
	fr: string;
	en: string;
}

export interface ITagPayload {
	value: ITagValue;
	color: string;
}

type TUseCreateTagOptions = {
	mutationConfig?: TMutationConfig<typeof createTag>;
};

/* -------------------------------------------------------------------------- */
/*                                     API                                     */
/* -------------------------------------------------------------------------- */
export const createTag = ({
	body,
}: {
	body: ITagPayload;
}): Promise<TOffsetTag> => {
	return apiClient.post('/offset_tags', body);
};

/* -------------------------------------------------------------------------- */
/*                                     Hook                                    */
/* -------------------------------------------------------------------------- */
export const useCreateTag = ({
	mutationConfig,
}: TUseCreateTagOptions = {}) => {
	const { onSuccess, onError, ...restConfig } =
		mutationConfig || {};

	return useMutation({
		mutationFn: createTag,
		onSuccess: (...args) => {
			queryClient.invalidateQueries({
				queryKey: ['offset-tags'],
			});
			notifications.show({
				title: i18n.t('global.success'),
				message: i18n.t('global.tagCreatedSuccess'),
				color: 'green',
			});
			onSuccess?.(...args);
		},
		onError: (...args) => {
			notifications.show({
				title: i18n.t('global.error'),
				message: i18n.t('global.somethingWentWrong'),
				color: 'var(--mantine-color-igluRed-9)',
			});
			onError?.(...args);
		},
		...restConfig,
	});
};
